import React, { useId, useRef, useState } from "react";

import { useTranslation } from "lib/i18n/use-translation";

import Info from "../Info";
import { useDashboardTracking } from "../tracking";
import styles from "./business-impact.module.scss";
import {
  formatMoney,
  formatMultipleOneDecimal,
  formatPercent,
  projectScenario,
} from "./business-impact-view";
import { RoiSample } from "./sample";

const K = "admin:companyDashboard.businessImpact";

// Keyed by version where it is used, so the slider starts again at each
// version's current participation.
const ScenarioModeller = ({ sample }: { sample: RoiSample }) => {
  const { t, i18n } = useTranslation(["admin"]);
  const locale = i18n.language;
  const currentRate = sample.scenarioModeller.currentParticipationPercent;
  const [rate, setRate] = useState(currentRate);
  const rateId = useId();
  const { filterChanged } = useDashboardTracking();
  // Whether the slider was used at all, never where it was left.
  const reported = useRef(false);
  const projection = projectScenario(sample, rate);
  const money = (amount: number) =>
    formatMoney(amount, sample.totalSavings.currency, locale);

  return (
    <section className={styles["card"]}>
      <div className={styles["modeller-head"]}>
        <div>
          <h3 className={styles["section-heading"]}>
            {t(`${K}.modeller.title`)}
          </h3>
          <p className={styles["subheading"]}>{t(`${K}.modeller.subtitle`)}</p>
        </div>
        <span className={styles["pill"]}>{t(`${K}.modeller.badge`)}</span>
      </div>

      <div className={styles["slider-panel"]}>
        <div className={styles["slider-head"]}>
          <span className={styles["slider-label"]}>
            <label htmlFor={rateId}>{t(`${K}.modeller.rateLabel`)}</label>
            <Info
              figure={t(`${K}.modeller.rateLabel`)}
              text={t(`${K}.modeller.rateInfo`)}
              align="start"
            />
          </span>
          <span className={styles["slider-value"]} aria-hidden="true">
            {formatPercent(rate, locale)}
          </span>
        </div>
        <input
          id={rateId}
          type="range"
          min={0}
          max={100}
          step={1}
          value={rate}
          aria-valuetext={formatPercent(rate, locale)}
          onChange={(event) => {
            if (!reported.current) {
              reported.current = true;
              filterChanged("business_impact", "participation");
            }
            setRate(Number(event.target.value));
          }}
          className={styles["slider"]}
        />
        <div className={styles["slider-scale"]}>
          <span>{formatPercent(0, locale)}</span>
          <span
            className={styles["slider-current"]}
            style={{ left: `${currentRate}%` }}
          >
            {t(`${K}.modeller.current`, {
              percent: formatPercent(currentRate, locale),
            })}
          </span>
          <span>{formatPercent(100, locale)}</span>
        </div>
      </div>

      <div className={styles["tiles"]}>
        <div
          role="group"
          aria-label={t(`${K}.modeller.roi`)}
          className={styles["tile"]}
        >
          <p className={styles["tile-title"]}>{t(`${K}.modeller.roi`)}</p>
          <p className={styles["tile-figure"]}>
            {formatMultipleOneDecimal(projection.roiMultiple, locale)}
            <span className={styles["tile-x"]}>
              {t(`${K}.summary.multipleSign`)}
            </span>
          </p>
        </div>
        <div
          role="group"
          aria-label={t(`${K}.modeller.savings`)}
          className={styles["tile"]}
        >
          <p className={styles["tile-title"]}>{t(`${K}.modeller.savings`)}</p>
          <p className={styles["tile-figure"]}>{money(projection.savings)}</p>
        </div>
      </div>
      <p className={styles["modeller-note"]}>{t(`${K}.modeller.note`)}</p>
    </section>
  );
};

export default ScenarioModeller;
